import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { t } from 'i18next';
import { toast } from 'sonner';
import { RadixButton, RadixDialog, RadixInput, RadixOtpInput, RadixSpinner } from '@/components/radix';
import { requestSetStoreLoginEmailOtpV3Api, verifySetStoreLoginEmailOtpV3Api } from '@/utils/Api/AuthV3';
import { useMediaQuery } from '../hooks';

export interface RegisterLoginEmailBlockingModalProps {
    open: boolean;
    /** Refetch profile after verify so the dialog can close when login_email is present. */
    onVerified?: () => void | Promise<void>;
}

export function RegisterLoginEmailBlockingModal({ open, onVerified }: RegisterLoginEmailBlockingModalProps) {
    const [awaitingOtp, setAwaitingOtp] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const validationSchema = useMemo(
        () =>
            awaitingOtp
                ? Yup.object({
                      login_email: Yup.string()
                          .email(t('Common.PleaseEnterValidEmail'))
                          .required(t('Common.EmailRequired')),
                      otp: Yup.string()
                          .required(t('Common.RegisterLoginEmailOtpRequired'))
                          .matches(/^\d{6}$/, t('Common.RegisterLoginEmailOtpLength')),
                  })
                : Yup.object({
                      login_email: Yup.string()
                          .email(t('Common.PleaseEnterValidEmail'))
                          .required(t('Common.EmailRequired')),
                      otp: Yup.string().strip(),
                  }),
        [awaitingOtp],
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { login_email: '', otp: '' },
        validationSchema,
        onSubmit: async (values) => {
            try {
                if (!awaitingOtp) {
                    await requestSetStoreLoginEmailOtpV3Api({ login_email: values.login_email.trim() });
                    toast.success(t('Common.OtpSentSuccess'));
                    setAwaitingOtp(true);
                    return;
                }

                await verifySetStoreLoginEmailOtpV3Api({
                    login_email: values.login_email.trim(),
                    otp: values.otp.replace(/\s/g, ''),
                });

                toast.success(t('Common.RegisterLoginEmailSuccess'));
                await onVerified?.();
                setAwaitingOtp(false);
                formik.resetForm({ values: { login_email: '', otp: '' } });
            } catch (error: any) {
                toast.error(error?.detail ?? error?.msg ?? t('Common.RegisterLoginEmailError'));
            }
        },
    });

    useEffect(() => {
        if (!open) return;
        setAwaitingOtp(false);
        formik.resetForm({ values: { login_email: '', otp: '' } });
        // formik is stable for resetForm; only re-run when dialog opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const id = window.requestAnimationFrame(() => {
            const idToFocus = awaitingOtp ? 'register-login-email-otp' : 'register-login-email';
            document.getElementById(idToFocus)?.focus();
        });
        return () => window.cancelAnimationFrame(id);
    }, [open, awaitingOtp]);

    const showSpinner = formik.isSubmitting;

    return (
        <RadixDialog
            open={open}
            onOpenChange={(next) => {
                if (!next) return;
            }}
            position={isDesktop ? 'center' : 'bottom'}
            preventDismiss
            showCloseButton={false}
            title={t('Common.RegisterLoginEmailTitle')}
            description={t('Common.RegisterLoginEmailDescription')}
            contentClassName="!p-0"
            className="md:max-w-[50%]  !z-[10001]"
            overlayClassName="!z-[10000] bg-black/70 dark:bg-black/80"
        >
            <div className="flex flex-col gap-4 px-4 pb-4 pt-0">
                <div
                    role="alert"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                >
                    {t('Common.RegisterLoginEmailAlertBody')}
                </div>

                <form className="flex flex-col gap-4" onSubmit={formik.handleSubmit} noValidate>
                    <RadixInput
                        id="register-login-email"
                        name="login_email"
                        type="email"
                        autoComplete="email"
                        label={t('Common.Email')}
                        placeholder={t('Common.Email')}
                        value={formik.values.login_email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.login_email && formik.errors.login_email
                                ? formik.errors.login_email
                                : undefined
                        }
                        disabled={formik.isSubmitting || awaitingOtp}
                        className="!rounded-xl !bg-white !h-12 !border-[#E6E1D9] focus:!border-primary-500"
                    />

                    {awaitingOtp ? (
                        <>
                            <RadixOtpInput
                                id="register-login-email-otp"
                                name="otp"
                                autoComplete="one-time-code"
                                label={t('Common.RegisterLoginEmailOtpLabel')}
                                value={formik.values.otp}
                                length={6}
                                fitContainer
                                onChange={(value) => {
                                    void formik.setFieldValue('otp', value);
                                }}
                                onBlur={formik.handleBlur}
                                error={formik.touched.otp && formik.errors.otp ? formik.errors.otp : undefined}
                                disabled={formik.isSubmitting}
                                inputClassName="!rounded-xl !bg-white !h-10 sm:!h-14 !w-full !text-2xl !font-bold !border-[#E6E1D9] focus:!border-primary-500 focus:!bg-white focus:!ring-4 focus:!ring-primary-500/10"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setAwaitingOtp(false);
                                    void formik.setFieldValue('otp', '');
                                    void formik.setFieldTouched('otp', false);
                                }}
                                className="self-start text-sm font-semibold text-primary-600 hover:text-primary-700 bg-transparent border-none cursor-pointer p-0"
                            >
                                {t('Common.RegisterLoginEmailChangeEmail')}
                            </button>
                        </>
                    ) : null}

                    <RadixButton
                        type="submit"
                        variant="primary"
                        className="w-full !h-12 !rounded-xl font-bold"
                        disabled={formik.isSubmitting}
                    >
                        {showSpinner ? (
                            <RadixSpinner variant="white" size="sm" />
                        ) : awaitingOtp ? (
                            t('Common.RegisterLoginEmailVerifyButton')
                        ) : (
                            t('Common.RegisterLoginEmailSendOtpButton')
                        )}
                    </RadixButton>
                </form>
            </div>
        </RadixDialog>
    );
}
